import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  ESTIMATED_FLOOR_LEVELS_GEOCOMM,
  VERTICAL_LOCATION_CONTENT_GEOCOMM,
} from '@rsos/capstone/src/constants/capabilities';
import { HistoricLocationsToggle } from '@rsos/components/capstone/LocationList/HistoricLocationsToggle';
import { CAPSTONE } from '@rsos/constants/appNames';
import { ALI_UNDERSCORE } from '@rsos/constants/locationTypes';
import { useBooleanFlagValue } from '@rsos/feature-flags';
import { useSelectedCall } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import { selectUserPermissionsByApp } from '@rsos/sinatra';
import checkEsriMapAccess from '@rsos/utils/checkEsriMapAccess';
import { getIsSecondaryLocation } from '@rsos/utils/getIsSecondaryLocation';
import { getMostRecentLocations } from '@rsos/utils/getMostRecentLoc';
import isValidCoordinates from '@rsos/utils/isValidCoordinates';
import useIsIRP from '@rsos/utils/useIsIRP';
import usePortalType from '@rsos/utils/usePortalType';
import { FullWidthContent } from '../JurisdictionView/EventPane/tabs/OverviewTab/OverviewTab.styles';
import UnplottableAddressNotice from '../JurisdictionView/Map/UnplottableAddressNotice';
import {
  getCivicAddress,
  isUnplottableAddress,
  UNGEOCODED_CALLS_FLAG,
} from '../JurisdictionView/Map/unplottableAddress';
import { isExtraInfoOrMeta } from '../JurisdictionView/helpers';
import { ReportAliDiscrepancyButton } from '../discrepancies';
import Altitude from './Altitude';
import EstimatedAddressLabel from './EstimatedAddressLabel';
import EstimatedFloorLevel from './EstimatedFloorLevel';
import LatLong from './LatLong';
import LocationAddress from './LocationAddress';
import LocationAddressProvider from './LocationAddressProvider';
import { AddressBox } from './MostRecentLocationCard.styles';
import SecondaryLocation from './SecondaryLocation';
import StreetView from './StreetView';
import Uncertainty from './Uncertainty';
import What3words from './What3words';
import { GOOGLE } from './constants/dataSources';
import { getDataSource } from './helpers';

/**
 * Most recent location card that is displayed in the overview tab in the event pane:
 * apps/emergency_data/src/components/JurisdictionView/EventPane/EventPane.js
 *
 * TODO: This component should eventually replace all the components used in:
 * apps/emergency_data/src/components/Location/*
 */
const MostRecentLocationCard = () => {
  const ungeocodedCallsEnabled = useBooleanFlagValue(
    UNGEOCODED_CALLS_FLAG,
    false,
  );
  const { irpVersion } = useIsIRP();

  const { isPremiumUser, portalType } = usePortalType();

  const activeCapabilities = useSelector(
    state => state.psaps.currentPSAP.activeCapabilities,
  );

  const permissions = useSelector(state =>
    selectUserPermissionsByApp(state.sinatra.user, CAPSTONE),
  );

  const hasEsriMapAccess =
    useSelector(state =>
      checkEsriMapAccess({
        psaps: state.psaps,
        sinatra: state.sinatra,
      }),
    ) && isPremiumUser;

  const hasEstimatedFloorLevelAccess = useMemo(
    () =>
      Boolean(
        activeCapabilities[ESTIMATED_FLOOR_LEVELS_GEOCOMM] &&
          permissions?.includes(
            activeCapabilities?.[
              ESTIMATED_FLOOR_LEVELS_GEOCOMM
            ]?.name?.toUpperCase(),
          ),
      ),
    [activeCapabilities, permissions],
  );

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );

  const selectedCall = useSelectedCall();

  const { locations = [], secondaryLocations = [] } = selectedCall?.core || {};

  const {
    mostRecentLocation: mostRecentPrimaryLocation,
    mostRecentSecondaryLocation,
    mostRecentSecondaryLocationIndex,
  } = useMemo(() => getMostRecentLocations(selectedCall), [selectedCall]);

  const mostRecentLocation = useMemo(() => {
    // If primaryLocation has no location favor the secondary location if it does
    if (
      !isValidCoordinates(mostRecentPrimaryLocation) &&
      isValidCoordinates(mostRecentSecondaryLocation)
    ) {
      return mostRecentSecondaryLocation;
    }

    return mostRecentPrimaryLocation || mostRecentSecondaryLocation;
  }, [mostRecentPrimaryLocation, mostRecentSecondaryLocation]);

  const { dataSource, isAniAli, isSecondaryLocation } = useMemo(() => {
    const dataSource = getDataSource(mostRecentLocation);

    const isAniAli =
      mostRecentLocation?.application?.startsWith(ALI_UNDERSCORE);
    const isSecondaryLocation = getIsSecondaryLocation(mostRecentLocation);

    return { dataSource, isAniAli, isSecondaryLocation };
  }, [mostRecentLocation]);

  const { payloadID, requestID } = useMemo(
    () => isExtraInfoOrMeta(mostRecentLocation),
    [mostRecentLocation],
  );

  const {
    altitude = '',
    altitude_uncertainty_radius = '',
    latitude = '',
    longitude = '',
    rg_floor = '',
    rg_exact_location = '',
    uncertainty_confidence = '',
    uncertainty_radius = '',
    rgAddressProvider = '',
    address_single_line = '',
  } = mostRecentLocation || {};

  const indoorLocation = rg_exact_location || rg_floor;

  const canDisplayEstimatedFloorLevel = useMemo(
    () => Boolean(dataSource === GOOGLE && hasEstimatedFloorLevelAccess),
    [dataSource, hasEstimatedFloorLevelAccess],
  );

  const shouldDisplayEstimatedFloorLevel = useMemo(
    () => Boolean(canDisplayEstimatedFloorLevel && altitude),
    [altitude, canDisplayEstimatedFloorLevel],
  );

  const isVerticalLocContentChecked = useMemo(
    () => Boolean(activeCapabilities[VERTICAL_LOCATION_CONTENT_GEOCOMM]),
    [activeCapabilities],
  );

  // SOUP-820: Always render ReportAliDiscrepancyButton so it can emit
  // diagnostic DOM when hidden (capability/permission/settings checks).
  // The component itself gates on application and the three access checks.

  if (!mostRecentLocation) return null;

  const unplottable =
    ungeocodedCallsEnabled && isUnplottableAddress(mostRecentLocation);

  return (
    <FullWidthContent
      backgroundColor="background"
      data-name="most-recent-location-card"
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          width: '100%',
          gap: '8px',
        }}
      >
        <AddressBox style={{ flex: 1, minWidth: 0 }}>
          <LocationAddress
            {...{ selectedCallerID }}
            isHoverable
            irpVersion={irpVersion}
            payloadID={payloadID}
            requestID={requestID}
          />
          {locations.length > 1 && (
            // TODO: Update imported components path to MostRecentLocationCard's components.
            <HistoricLocationsToggle
              irpVersion={irpVersion}
              locations={locations}
            />
          )}
        </AddressBox>
        <ReportAliDiscrepancyButton
          location={mostRecentLocation}
          phoneNumber={selectedCallerID}
        />
      </div>
      {unplottable ? (
        <UnplottableAddressNotice
          address={getCivicAddress(mostRecentLocation)}
        />
      ) : (
        <>
          <LatLong
            irpVersion={irpVersion}
            isHoverable
            latitude={latitude}
            longitude={longitude}
            payloadID={payloadID}
            requestID={requestID}
          />
          <What3words
            irpVersion={irpVersion}
            isHoverable
            location={mostRecentLocation}
            payloadID={payloadID}
            requestID={requestID}
          />
          <Uncertainty
            confidence={uncertainty_confidence}
            irpVersion={irpVersion}
            isHoverable
            payloadID={payloadID}
            requestID={requestID}
            uncertaintyRadius={uncertainty_radius}
          />
          <Altitude
            altitude={altitude}
            altitudeUncertaintyRadius={altitude_uncertainty_radius}
            indoorLocation={indoorLocation}
            irpVersion={irpVersion}
            isHoverable
            payloadID={payloadID}
            requestID={requestID}
          />
          <StreetView
            irpVersion={irpVersion}
            isEsriMap={hasEsriMapAccess}
            latitude={latitude}
            longitude={longitude}
            payloadID={payloadID}
            portalType={portalType}
            requestID={requestID}
            isHoverable
          />
          {shouldDisplayEstimatedFloorLevel && (
            <EstimatedFloorLevel
              altitude={altitude}
              altitudeUncertaintyRadius={altitude_uncertainty_radius}
              irpVersion={irpVersion}
              isHoverable
              isVerticalLocContentChecked={isVerticalLocContentChecked}
              latitude={latitude}
              longitude={longitude}
              payloadID={payloadID}
              portalType={portalType}
              requestID={requestID}
              uncertaintyRadius={uncertainty_radius}
            />
          )}
        </>
      )}
      {!isAniAli && (
        <LocationAddressProvider rgAddressProvider={rgAddressProvider} />
      )}
      {!isSecondaryLocation && secondaryLocations.length > 0 && (
        <SecondaryLocation
          secondaryLocation={mostRecentSecondaryLocation}
          secondaryLocationIndex={mostRecentSecondaryLocationIndex}
        />
      )}
      <EstimatedAddressLabel
        isMostRecent
        addressSingleLine={address_single_line}
        classOfServiceCategory={mostRecentLocation?.class_of_service_category}
        classOfService={mostRecentLocation?.class_of_service}
      />
    </FullWidthContent>
  );
};

export default MostRecentLocationCard;
